import { createMemo, createSignal, onCleanup, onMount, For, Show, type Accessor } from "solid-js";
import { FiSearch } from "solid-icons/fi";

import {
  buildChartSearchResults,
  collectChartSearchTargets,
  scrollToChartSearchTarget,
  CHART_SEARCH_VISIBLE_RESULTS,
  type ChartSearchRow,
  type ChartSearchTarget,
} from "../../utils/chartSearchTargets";
import { clampHighlight, nextHighlightForKey } from "../../utils/listKeyboardNav";

export interface SidebarChartSearchProps {
  isCollapsed: Accessor<boolean>;
  isMobile: Accessor<boolean>;
  /** Expand a collapsed sidebar so the input can take focus. */
  onExpandSidebar: () => void;
  /** Close the mobile drawer after a pick, so the scrolled-to chart is actually visible. */
  onAfterPick?: () => void;
}

/**
 * Jump-to-chart search for the maneuver and performance report pages.
 *
 * The index is read from the DOM at search time (see utils/chartSearchTargets) rather than pushed
 * into a store, so it always reflects the charts actually on screen. The results list renders in
 * normal flow directly below the input: capping it at three rows is what makes that possible, and
 * it avoids both a z-index stack and the `.sidebar { overflow: hidden }` clipping a floating popup
 * would hit. Because .sidebar-bottom is anchored at the bottom of the sidebar, the rows appear
 * below the input and above the Share button, and the block grows upward as they appear.
 */
const SidebarChartSearch = (props: SidebarChartSearchProps) => {
  const [query, setQuery] = createSignal("");
  const [targets, setTargets] = createSignal<ChartSearchTarget[]>([]);
  /** -1 means "nothing highlighted": the first ArrowDown selects row 0. */
  const [highlight, setHighlight] = createSignal(-1);
  const [open, setOpen] = createSignal(false);

  let inputEl: HTMLInputElement | undefined;
  let rootEl: HTMLDivElement | undefined;

  /** Icon-only when the desktop sidebar is collapsed; the mobile drawer is always full width. */
  const collapsedIcon = () => props.isCollapsed() && !props.isMobile();

  const refreshTargets = () => setTargets(collectChartSearchTargets());

  const results = createMemo(() => buildChartSearchResults(targets(), query()));
  const rows = () => results().rows;
  const hiddenCount = () => Math.max(0, results().total - rows().length);
  const clamped = createMemo(() => clampHighlight(highlight(), rows().length));
  const showMenu = () => open() && query().trim().length > 0;

  const pick = (row: ChartSearchRow) => {
    scrollToChartSearchTarget(row.target);
    setOpen(false);
    // Blur so Space / PageDown go back to scrolling the page rather than typing.
    inputEl?.blur();
    props.onAfterPick?.();
  };

  const onSearchKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Escape") {
      e.preventDefault();
      // Clear first, close only when already empty — same policy as the chart library panel.
      if (query()) {
        setQuery("");
        setHighlight(-1);
      } else {
        setOpen(false);
        inputEl?.blur();
      }
      return;
    }

    if (e.key === "Enter") {
      e.preventDefault();
      const row = rows()[clamped()];
      if (row) {pick(row);}
      return;
    }

    const stepped = nextHighlightForKey(e.key, clamped(), rows().length);
    if (stepped !== null) {
      e.preventDefault();
      setHighlight(stepped);
    }
  };

  onMount(() => {
    const onPointerDown = (e: PointerEvent) => {
      if (rootEl && !rootEl.contains(e.target as Node)) {setOpen(false);}
    };
    document.addEventListener("pointerdown", onPointerDown);
    onCleanup(() => document.removeEventListener("pointerdown", onPointerDown));
  });

  return (
    <div
      class="sidebar-chart-search"
      classList={{ "sidebar-chart-search--collapsed": collapsedIcon() }}
      ref={(el) => (rootEl = el)}
    >
      <Show
        when={!collapsedIcon()}
        fallback={
          <button
            type="button"
            class="menu-item sidebar-chart-search__icon-button"
            title="Find chart"
            aria-label="Find chart"
            onClick={() => {
              props.onExpandSidebar();
              queueMicrotask(() => inputEl?.focus());
            }}
          >
            <FiSearch size={20} />
          </button>
        }
      >
        <input
          ref={(el) => (inputEl = el)}
          type="text"
          class="sidebar-chart-search__input"
          role="combobox"
          aria-expanded={showMenu()}
          aria-controls="sidebar-chart-search-menu"
          aria-activedescendant={
            showMenu() && clamped() >= 0 ? `sidebar-chart-search-opt-${clamped()}` : undefined
          }
          aria-label="Find a chart on this page"
          placeholder="Find chart..."
          autocomplete="off"
          value={query()}
          onFocus={() => {
            refreshTargets();
            setOpen(true);
          }}
          onInput={(e) => {
            // Re-read the DOM per keystroke: charts appear and disappear as the page loads.
            refreshTargets();
            setQuery((e.target as HTMLInputElement).value);
            setHighlight(-1);
            setOpen(true);
          }}
          onKeyDown={onSearchKeyDown}
        />

        <Show when={showMenu()}>
          <ul id="sidebar-chart-search-menu" class="sidebar-chart-search__menu" role="listbox">
            <For each={rows()}>
              {(row, i) => (
                // Keyboard selection lives on the combobox input (aria-activedescendant), not the rows.
                // eslint-disable-next-line jsx-a11y/click-events-have-key-events
                <li
                  id={`sidebar-chart-search-opt-${i()}`}
                  role="option"
                  aria-selected={i() === clamped()}
                  class="sidebar-chart-search__option"
                  classList={{ "sidebar-chart-search__option--active": i() === clamped() }}
                  onMouseEnter={() => setHighlight(i())}
                  // Keep focus in the input so the click is never lost to a blur.
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => pick(row)}
                >
                  <span class="sidebar-chart-search__title">
                    {row.title}
                    <Show when={row.count > 1}>
                      <span class="sidebar-chart-search__count">×{row.count}</span>
                    </Show>
                  </span>
                  <Show when={row.detail}>
                    <span class="sidebar-chart-search__sub">{row.detail}</span>
                  </Show>
                </li>
              )}
            </For>

            <Show when={rows().length === 0}>
              <li class="sidebar-chart-search__empty">No matching charts</li>
            </Show>

            <Show when={hiddenCount() > 0}>
              <li class="sidebar-chart-search__more">
                +{hiddenCount()} more — keep typing
              </li>
            </Show>
          </ul>
        </Show>
      </Show>
    </div>
  );
};

export default SidebarChartSearch;
export { CHART_SEARCH_VISIBLE_RESULTS };
